import React, { useEffect, useState, useCallback } from 'react';
import { MessageSquare, Send, Trash2, Lock, Megaphone, ChevronLeft, LifeBuoy } from 'lucide-react';
import MainLayout from '../layout/MainLayout';
import { apiService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/useToast';
import './mensajeria.scss';

interface Msg { id: number; message: string; mine: boolean; sender_name: string; is_read: boolean; created_at: string; }
interface SupportRow { user_id: number; name: string; last: string | null; last_at: string | null; unread: number; }

const fmt = (sql?: string | null) => {
  if (!sql) return '';
  const d = new Date(String(sql).replace(' ', 'T'));
  if (isNaN(d.getTime())) return String(sql);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  return sameDay ? d.toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString('es', { day: '2-digit', month: 'short' });
};

const MensajeriaPage: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role_name === 'admin' || user?.role_name === 'super_admin';

  return (
    <MainLayout>
      {isAdmin ? <AdminInbox /> : <SupportChat />}
    </MainLayout>
  );
};

/**
 * Mentores y mentees: un único canal, el de soporte con el equipo del programa.
 * Los avisos del sistema viven en la campana y la dupla conversa en sus sesiones.
 */
const SupportChat: React.FC = () => {
  const { error: showError } = useToast();
  const [thread, setThread] = useState<Msg[]>([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await apiService.messages.getThread('support');
      setThread(r.data?.data || []);
    } catch {
      showError('No se pudo cargar la conversación');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => { load(); }, [load]);

  const send = async () => {
    const text = draft.trim();
    if (!text) return;
    setBusy(true);
    try {
      await apiService.messages.send({ channel: 'support', message: text });
      setDraft('');
      await load();
    } catch (e: any) {
      showError(e?.response?.data?.message || 'No se pudo enviar');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: number) => {
    try { await apiService.messages.remove(id); setThread(t => t.filter(m => m.id !== id)); }
    catch { showError('No se pudo eliminar'); }
  };

  return (
    <div className="mensajeria">
      <div className="eyebrow">Mensajería</div>
      <h1 className="heading-1">Equipo del <em>programa</em></h1>

      <div className="mzg-intro">
        <span className="mzg-intro__icon"><LifeBuoy size={20} /></span>
        <div className="mzg-intro__body">
          <p className="mzg-intro__lead">
            Escríbenos cuando tengas una duda o necesites ayuda con el programa. Te responde el equipo que
            coordina la mentoría.
          </p>
          <ul className="mzg-intro__list">
            <li><b>Sobre el programa:</b> plazos, etapas, qué se espera de ti.</li>
            <li><b>Sobre la plataforma:</b> acceso, el acuerdo, el FODA, el PDI o las sesiones.</li>
            <li><b>Sobre tu dupla:</b> si algo no está funcionando y quieres comentarlo.</li>
          </ul>
          <p className="mzg-intro__privacy">
            <Lock size={13} /> Es confidencial: tu {'mentor o mentee'} no ve estos mensajes.
          </p>
        </div>
      </div>

      <section className="mzg-panel mzg-panel--solo">
        <div className="mzg-thread">
          <div className="mzg-msgs">
            {loading ? (
              <p className="mzg-empty">Cargando…</p>
            ) : thread.length === 0 ? (
              <p className="mzg-empty">Todavía no has escrito. Cuéntanos en qué te podemos ayudar.</p>
            ) : thread.map(m => (
              <div className={`mzg-bubble ${m.mine ? 'mine' : ''}`} key={m.id}>
                {!m.mine && <span className="mzg-bubble__who">{m.sender_name} · equipo del programa</span>}
                <span className="mzg-bubble__text">{m.message}</span>
                <span className="mzg-bubble__meta">
                  {fmt(m.created_at)}
                  {m.mine && <button className="mzg-del" onClick={() => remove(m.id)} title="Eliminar"><Trash2 size={12} /></button>}
                </span>
              </div>
            ))}
          </div>
          <div className="mzg-composer">
            <textarea className="mzg-textarea" rows={2} placeholder="Escribe tu consulta al equipo del programa…" value={draft}
              onChange={e => setDraft(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }} />
            <button className="mzg-send" disabled={busy || !draft.trim()} onClick={send} title="Enviar"><Send size={16} /></button>
          </div>
        </div>
      </section>
    </div>
  );
};

/** Administradores: anuncios y la bandeja de soporte de cada usuario. */
const AdminInbox: React.FC = () => {
  const { error: showError, success } = useToast();
  const [supportRows, setSupportRows] = useState<SupportRow[]>([]);
  const [active, setActive] = useState<string | null>(null); // 'announce' | 'admin:<userId>'
  const [thread, setThread] = useState<Msg[]>([]);
  const [draft, setDraft] = useState('');
  const [annTitle, setAnnTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showThreadMobile, setShowThreadMobile] = useState(false);

  const loadLists = useCallback(async () => {
    try {
      setLoading(true);
      const s = await apiService.messages.supportInbox().catch(() => null);
      setSupportRows(s?.data?.data || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadLists(); }, [loadLists]);

  const openConv = async (key: string) => {
    setActive(key); setThread([]); setShowThreadMobile(true);
    if (key === 'announce') return;
    try {
      const uid = Number(key.split(':')[1]);
      const r = await apiService.messages.supportThread(uid);
      setThread(r.data?.data || []);
      loadLists();
    } catch { showError('No se pudo abrir la conversación'); }
  };

  const sendMsg = async () => {
    const text = draft.trim();
    if (!text || !active || !active.startsWith('admin:')) return;
    setBusy(true);
    try {
      const uid = Number(active.split(':')[1]);
      await apiService.messages.supportReply({ user_id: uid, message: text });
      const r = await apiService.messages.supportThread(uid); setThread(r.data?.data || []);
      setDraft(''); loadLists();
    } catch (e: any) { showError(e?.response?.data?.message || 'No se pudo enviar'); }
    finally { setBusy(false); }
  };

  const sendAnnounce = async () => {
    if (!annTitle.trim() || !draft.trim()) { showError('Título y mensaje requeridos'); return; }
    setBusy(true);
    try {
      const r = await apiService.messages.announce({ title: annTitle.trim(), message: draft.trim() });
      success(r.data?.message || 'Anuncio enviado');
      setAnnTitle(''); setDraft('');
    } catch (e: any) { showError(e?.response?.data?.message || 'No se pudo enviar el anuncio'); }
    finally { setBusy(false); }
  };

  const delMsg = async (id: number) => {
    try { await apiService.messages.remove(id); setThread(t => t.filter(m => m.id !== id)); loadLists(); }
    catch { showError('No se pudo eliminar'); }
  };

  const listItems = [
    { key: 'announce', icon: <Megaphone size={18} />, title: 'Enviar anuncio', subtitle: 'A todos los usuarios', last: null as string | null, last_at: null as string | null, unread: 0 },
    ...supportRows.map(s => ({ key: `admin:${s.user_id}`, icon: <MessageSquare size={18} />, title: s.name, subtitle: 'Soporte', last: s.last, last_at: s.last_at, unread: s.unread })),
  ];
  const activeMeta = listItems.find(i => i.key === active);

  return (
    <div className="mensajeria">
      <div className="eyebrow">Mensajería</div>
      <h1 className="heading-1">Tu <em>buzón</em></h1>
      <p className="mensajeria__lead">Consultas de soporte de mentores y mentees, y anuncios del programa.</p>

      <div className={`mzg ${showThreadMobile ? 'show-thread' : ''}`}>
        <aside className="mzg-list">
          {loading ? <p className="mzg-empty">Cargando…</p> : listItems.map(it => (
            <button key={it.key} className={`mzg-conv ${active === it.key ? 'is-active' : ''}`} onClick={() => openConv(it.key)}>
              <span className="mzg-conv__icon">{it.icon}</span>
              <span className="mzg-conv__body">
                <span className="mzg-conv__title">{it.title}</span>
                <span className="mzg-conv__last">{it.last || it.subtitle}</span>
              </span>
              <span className="mzg-conv__meta">
                {it.last_at && <span className="mzg-conv__time">{fmt(it.last_at)}</span>}
                {!!it.unread && <span className="mzg-conv__badge">{it.unread}</span>}
              </span>
            </button>
          ))}
        </aside>

        <section className="mzg-panel">
          {!active ? (
            <div className="mzg-blank"><MessageSquare size={34} /><p>Selecciona una conversación</p></div>
          ) : active === 'announce' ? (
            <div className="mzg-compose-ann">
              <button className="mzg-back" onClick={() => setShowThreadMobile(false)}><ChevronLeft size={16} /> Volver</button>
              <h3 className="mzg-panel__title"><Megaphone size={18} /> Enviar anuncio a todos</h3>
              <input className="mzg-input" placeholder="Título del anuncio" value={annTitle} onChange={e => setAnnTitle(e.target.value)} />
              <textarea className="mzg-textarea" rows={6} placeholder="Escribe el mensaje…" value={draft} onChange={e => setDraft(e.target.value)} />
              <button className="btn btn--primary" disabled={busy} onClick={sendAnnounce}><Send size={15} /> Enviar anuncio</button>
            </div>
          ) : (
            <div className="mzg-thread">
              <div className="mzg-panel__head">
                <button className="mzg-back" onClick={() => setShowThreadMobile(false)}><ChevronLeft size={16} /></button>
                <div><div className="mzg-panel__title">{activeMeta?.title}</div><div className="mzg-panel__sub">Soporte · confidencial</div></div>
              </div>
              <div className="mzg-msgs">
                {thread.length === 0 ? <p className="mzg-empty">Aún no hay mensajes.</p> : thread.map(m => (
                  <div className={`mzg-bubble ${m.mine ? 'mine' : ''}`} key={m.id}>
                    {!m.mine && <span className="mzg-bubble__who">{m.sender_name}</span>}
                    <span className="mzg-bubble__text">{m.message}</span>
                    <span className="mzg-bubble__meta">{fmt(m.created_at)}{m.mine && <button className="mzg-del" onClick={() => delMsg(m.id)} title="Eliminar"><Trash2 size={12} /></button>}</span>
                  </div>
                ))}
              </div>
              <div className="mzg-composer">
                <textarea className="mzg-textarea" rows={2} placeholder="Escribe una respuesta…" value={draft}
                  onChange={e => setDraft(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMsg(); } }} />
                <button className="mzg-send" disabled={busy || !draft.trim()} onClick={sendMsg}><Send size={16} /></button>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default MensajeriaPage;
